// import react packages;
import React, { useContext, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import PropTypes from "prop-types";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// Import Polaris Components
import { PageActions, Stack, TextField } from "@shopify/polaris";

// import helper
import { OnboardingContext, PrivateContext } from "lib/context";
import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";

import { IS_WOOCOMMERCE_STORE_CONNECTED } from "app/product/apollo/mutations";

import SyncWooCommerceProduct from "app/product/modules/provider/features/add/subFeatures/syncWooCommerceProduct";

// import components
import TermModal from "app/product/modules/provider/features/genric/term/term";
// import newCMSData from "app/v1/products/modules/provider/features/add/subFeatures/wixConnect/cms/cms.json";
import ConnectedShop from "./subFeatureItems/connected";

const VendorConnectWoocommerce = (props) => {
  const { currentUser: currentUserData, history, cms } = useContext(PrivateContext);
  const { wooCommerce: wooCommerceConfig = {} } = (currentUserData && currentUserData.storeConfig) || {};
  const { shop = "", accessKey = "", accessToken = "" } = wooCommerceConfig || {};
  const { isOnboarding, onPrevious } = useContext(OnboardingContext);
  const { CRITICAL, SUCCESS } = constant;
  const { setBanner } = props;
  const [value, setValue] = useState({
    consumerKey: accessKey || "",
    consumerSecret: accessToken || "",
    shopDomain: shop || "",
  });
  const [isConnected, setIsConnected] = useState(!!shop);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    shopDomain: "",
    consumerKey: "",
    consumerSecret: "",
  });

  const [isWooCommerceConnected, { loading }] = useMutation(IS_WOOCOMMERCE_STORE_CONNECTED);
  const { IS_WOOCOMMERCE_STORE_CONNECTED: STORE_CONNECT } = constant.gql;

  const onSubmit = async () => {
    const { consumerKey = "", consumerSecret = "", shopDomain = "" } = value;
    const isValidDomainPath = baseHelper.isValidDomainPath(shopDomain.trim());
    if (!isValidDomainPath) {
      setBanner({ isOpen: true, status: CRITICAL, title: cms("section.connect.message.error.invalidShop") });
      setIsOpen(false);
      return;
    }
    try {
      const response = await isWooCommerceConnected({
        variables: {
          input: {
            consumerKey: (consumerKey || "").trim(),
            consumerSecret: (consumerSecret || "").trim(),
            shopDomain: (shopDomain || "").trim(),
          },
        },
      });
      const responseError = baseHelper.getResponseError(response.data, STORE_CONNECT);
      if (responseError) {
        setBanner({ isOpen: true, status: CRITICAL, title: responseError });
        setIsOpen(false);
        return;
      }
      const responseData = baseHelper.getResponseData(response.data, STORE_CONNECT);
      if (responseData) {
        setIsConnected(true);
        setBanner({ isOpen: true, status: SUCCESS, title: `${cms("section.connect.message.success.connected")}` });
        setIsOpen(false);
        return;
      }
    } catch (exception) {
      setBanner({ isOpen: true, status: CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const handleChange = (fieldName, fieldValue) => {
    setValue({
      ...value,
      [fieldName]: (fieldValue || "").trim(),
    });
  };

  const handleTermModal = (fieldName = "check", modalValue = "") => {
    if (!value.shopDomain || !value.consumerSecret || !value.consumerKey) {
      setIsOpen(false);
      const errorMessages = {
        consumerKey: cms("section.connect.message.error.key"),
        consumerSecret: cms("section.connect.message.error.secret"),
        shopDomain: cms("section.connect.message.error.domain"),
      };
      setErrorMessage({
        ...errorMessage,
        [fieldName]: !value[fieldName] ? errorMessages[fieldName] : "",
      });

      return;
    }
    if (modalValue === constant.CONNECT) {
      setIsOpen(true);
    }
    setErrorMessage("");
  };

  return (
    <>
      <TermModal
        cms={cms}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        shopDomain={value.shopDomain}
        setBanner={setBanner}
        loading={loading}
        onSubmit={onSubmit}
        item="WooCommerce"
      />
      <TextField
        label={`${cms("section.connect.label.shopDomain")}*`}
        error={errorMessage.shopDomain}
        id="shopInput"
        prefix="https://"
        placeholder={cms("section.connect.placeholder.demo")}
        type={constant.TEXT}
        value={value.shopDomain}
        onChange={(inputValue) => handleChange(constant.SHOP_DOMAIN, inputValue)}
        onBlur={() => handleTermModal(constant.SHOP_DOMAIN)}
        disabled={isConnected}
      />
      {!isConnected ? (
        <>
          <br />
          <TextField
            label={`${cms("section.connect.label.wooCommerceConsumerKey")}*`}
            error={errorMessage.consumerKey}
            id="consumerKey"
            placeholder={cms("section.connect.placeholder.key")}
            type={constant.TEXT}
            value={value.consumerKey}
            onChange={(inputValue) => handleChange(constant.CONSUMER_KEY, inputValue)}
            onBlur={() => handleTermModal(constant.CONSUMER_KEY)}
            disabled={isConnected}
          />
        </>
      ) : null}
      {!isConnected ? (
        <>
          <br />
          <TextField
            label={`${cms("section.connect.label.wooCommerceConsumerSecret")}*`}
            error={errorMessage.consumerSecret}
            id="consumerSecret"
            placeholder={cms("section.connect.placeholder.secret")}
            type={constant.TEXT}
            value={value.consumerSecret}
            onChange={(inputValue) => handleChange(constant.CONSUMER_SECRET, inputValue)}
            onBlur={() => handleTermModal(constant.CONSUMER_SECRET)}
            disabled={isConnected}
          />
        </>
      ) : null}
      {isConnected && (
        <>
          <br />
          <Stack>
            <Stack.Item fill>
              <ConnectedShop
                cms={cms}
                currentUserData={currentUserData}
                setBanner={setBanner}
                setIsConnected={setIsConnected}
                setValue={setValue}
                value={value}
              />
            </Stack.Item>
            <Stack.Item>
              <SyncWooCommerceProduct history={history} setBanner={setBanner} cms={cms} />
            </Stack.Item>
          </Stack>
        </>
      )}
      {!isConnected && (
        <PageActions
          primaryAction={{
            id: "connectShop",
            content: `${cms("section.connect.button.connect")}`,
            onAction: () => handleTermModal("check ", constant.CONNECT),
            disabled: isConnected,
          }}
          secondaryActions={[
            {
              id: "cancelAction",
              content: `${cms("section.connect.button.cancel")}`,
              onAction: () => (isOnboarding && onPrevious()) || history.push("/products"),
            },
          ]}
        />
      )}
    </>
  );
};

VendorConnectWoocommerce.propTypes = {
  setBanner: PropTypes.func,
};

VendorConnectWoocommerce.defaultProps = {
  setBanner: () => {},
};

export default withFeature(withErrorBoundary(VendorConnectWoocommerce), {
  feature: constant.PRODUCT_IMPORT_WOOCOMMERCE,
  // subFeature: constant.PRODUCT_IMPORT_WOOCOMMERCE,
});
